import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  Database, Download, Upload, Trash2, Clock, RefreshCw, AlertCircle,
  HardDrive, CheckCircle, FileArchive, Shield, Mail, Loader2,
} from "lucide-react";

import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminBackupPage() {
  const { api } = useAuth();
  const [backups, setBackups] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const fileRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const [backupsRes, configRes] = await Promise.all([
        api.get("/admin/backups"),
        api.get("/admin/backups/config"),
      ]);
      setBackups(backupsRes.data.backups || []);
      setConfig(configRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const res = await api.post("/admin/backups/create");
      toast.success(`Sauvegarde creee ! ${res.data.total_documents} documents dans ${Object.keys(res.data.collections).length} collections.`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
    setCreating(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post("/admin/backups/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Fichier importe !");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'import");
    }
    e.target.value = "";
  };

  const handleSendEmail = async () => {
    setSendingEmail(true);
    setEmailResult(null);
    try {
      const res = await api.post("/admin/backups/send-email");
      setEmailResult(res.data);
      fetchData();
    } catch (err) {
      setEmailResult({ error: err.response?.data?.detail || "Erreur d'envoi" });
    }
    setSendingEmail(false);
  };

  const handleDownload = async (filename) => {
    try {
      const res = await api.get(`/admin/backups/download/${filename}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Erreur de telechargement");
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Supprimer ${filename} ?`)) return;
    try {
      await api.delete(`/admin/backups/${filename}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleRestore = async (filename) => {
    if (!window.confirm(`ATTENTION: Restaurer ${filename} va REMPLACER toutes les donnees actuelles. Continuer ?`)) return;
    if (!window.confirm("Etes-vous ABSOLUMENT SUR ? Cette action est irreversible.")) return;
    setRestoring(filename);
    try {
      const res = await api.post(`/admin/backups/restore/${filename}`);
      toast.success(`Restauration terminee ! ${res.data.total_documents} documents dans ${Object.keys(res.data.restored_collections).length} collections.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de restauration");
    }
    setRestoring(null);
  };

  return (
    <div className="space-y-6" data-testid="admin-backup-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading font-extrabold text-xl text-foreground flex items-center gap-2">
            <Database className="w-6 h-6 text-[#FF6B35]" />
            Sauvegardes
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">Gestion des sauvegardes MongoDB</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 bg-card border border-border px-4 py-2 rounded-full text-sm font-medium text-foreground hover:border-[#FF6B35]/40 transition-colors"
            data-testid="upload-backup-btn"
          >
            <Upload className="w-4 h-4" /> Importer
          </button>
          <input ref={fileRef} type="file" accept=".json.gz,.tar.gz" onChange={handleUpload} className="hidden" />
          <button
            onClick={handleCreate}
            disabled={creating}
            className="flex items-center gap-1.5 bg-[#FF6B35] text-white px-4 py-2 rounded-full text-sm font-bold hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="create-backup-btn"
          >
            <HardDrive className="w-4 h-4" /> {creating ? "..." : "Creer"}
          </button>
        </div>
      </div>

      {/* Config */}
      {config && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] text-muted-foreground uppercase">Base de donnees</p>
            <p className="font-heading font-bold text-foreground mt-0.5">{config.db_name}</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] text-muted-foreground uppercase">Total sauvegardes</p>
            <p className="font-heading font-bold text-foreground mt-0.5">{config.total_backups}</p>
          </div>
          <div className="bg-card border border-border rounded-2xl p-4">
            <p className="text-[10px] text-muted-foreground uppercase">Planning</p>
            <p className="font-heading font-bold text-foreground mt-0.5 text-xs">{config.schedule}</p>
          </div>
        </div>
      )}

      {/* Email backup */}
      <div className="bg-card border border-border rounded-2xl p-4" data-testid="backup-email-widget">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <Mail className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-sm text-foreground">Sauvegarde + Envoi par email</h3>
              <p className="text-[10px] text-muted-foreground">Cree une sauvegarde et l'envoie a contact@reallyfan.com</p>
            </div>
          </div>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            className="flex items-center gap-1.5 bg-blue-500 text-white px-5 py-2.5 rounded-full text-sm font-bold hover:scale-105 active:scale-95 transition-transform disabled:opacity-50"
            data-testid="send-backup-email-btn"
          >
            {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            {sendingEmail ? "Envoi..." : "Sauvegarder + Email"}
          </button>
        </div>
        {emailResult && !emailResult.error && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-3 mt-3">
            <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0" />
            <p className="text-sm text-emerald-700">
              Sauvegarde <strong>{emailResult.filename}</strong> creee ({emailResult.total_documents} documents).
              {emailResult.email_sent ? " Email envoye avec succes !" : " Erreur lors de l'envoi email."}
            </p>
          </div>
        )}
        {emailResult?.error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-3 mt-3">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{emailResult.error}</p>
          </div>
        )}
      </div>

      {/* Backups list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2].map(i => <div key={i} className="bg-card rounded-2xl h-16 animate-pulse" />)}
        </div>
      ) : backups.length === 0 ? (
        <div className="text-center py-12">
          <Database className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Aucune sauvegarde</p>
        </div>
      ) : (
        <div className="space-y-2">
          {backups.map((b) => (
            <div key={b.filename} className="bg-card border border-border rounded-2xl p-4 flex items-center gap-4" data-testid={`backup-${b.filename}`}>
              <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center shrink-0">
                <FileArchive className="w-5 h-5 text-[#FF6B35]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-foreground truncate">{b.filename}</p>
                <div className="flex items-center gap-3 text-[10px] text-muted-foreground mt-0.5">
                  <span className="flex items-center gap-1"><HardDrive className="w-3 h-3" /> {b.size_formatted}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {new Date(b.created_at).toLocaleString("fr-FR")}</span>
                  <span className="bg-muted px-1.5 py-0.5 rounded uppercase font-bold">{b.type}</span>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button
                  onClick={() => handleDownload(b.filename)}
                  className="p-2 rounded-lg bg-muted text-muted-foreground hover:text-[#FF6B35] transition-colors"
                  title="Telecharger"
                >
                  <Download className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleRestore(b.filename)}
                  disabled={restoring === b.filename}
                  className="p-2 rounded-lg bg-muted text-muted-foreground hover:text-amber-500 transition-colors disabled:opacity-50"
                  title="Restaurer"
                >
                  <RefreshCw className={`w-4 h-4 ${restoring === b.filename ? "animate-spin" : ""}`} />
                </button>
                <button
                  onClick={() => handleDelete(b.filename)}
                  className="p-2 rounded-lg bg-muted text-muted-foreground hover:text-red-400 transition-colors"
                  title="Supprimer"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="bg-amber-500/10 border border-amber-500/20 rounded-2xl p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
        <div className="text-xs text-foreground/70">
          <p className="font-bold text-foreground">Attention</p>
          <p>La restauration remplace TOUTES les donnees existantes. Creez toujours une sauvegarde avant de restaurer.</p>
        </div>
      </div>
    </div>
  );
}
